import React from "react";

import styled from "@emotion/styled";

import cls from "../../../../../utils/cls";
import { toReadableDate } from "../../../../../utils/DateHelpers";
import {
    formattedToNumber,
    roundNumber,
} from "../../../../../utils/NumberHelpers";
import resolveWebView from "../../../../../utils/webViewRedirect";
import { navigate } from "../../../../shared/LinkComponent";
import Margin from "../../../../shared/Margin";
import NewHeadline from "../../../../shared/NewHeadline";
import Section from "../../../../shared/Section";
import useSnackbar from "../../../../shared/Snackbar/store";
import useCalculatorStore from "../../CalculatorPage/store";
import useStepButtonsStore from "../StepButtons/store";
import ContactData from "../StepDeliveryAddress/ContactData";
import DeliveryAddress from "../StepDeliveryAddress/DeliveryAddress";
import InvoiceAddress from "../StepDeliveryAddress/InvoiceAddress";
import MultipleUnloadingPoints from "../StepDeliveryAddress/MultipleUnloadingPoints";
import useStepDeliveryAddressStore from "../StepDeliveryAddress/store";
import AntiFreeze from "../StepDeliveryDetails/AntiFreeze";
import ClimateNeutral from "../StepDeliveryDetails/ClimateNeutral";
import Comment from "../StepDeliveryDetails/Comment";
import DeliveryTime from "../StepDeliveryDetails/DeliveryTime";
import Hose from "../StepDeliveryDetails/Hose";
import useStepDeliveryDetailsStore from "../StepDeliveryDetails/store";
import StepPaymentType from "../StepPaymentType";
import useStepPaymentTypeStore from "../StepPaymentType/store";
import StepProductChoice from "../StepProductChoice";
import useStepProductChoiceStore from "../StepProductChoice/store";
import useCheckoutStore from "../store";
import Agb from "./Agb";
import Amount from "./Amount";
import useAmountStore from "./Amount/store";
import CheckoutIsExpired from "./CheckoutIsExpired";
import Newsletter from "./Newsletter";
import OrderChangableDetail from "./OrderChangableDetail";
import useStepOverviewStore from "./store";
import * as styles from "./style.module.scss";
import Voucher from "./Voucher";
import useVoucherStore from "./Voucher/store";

const Wrapper = styled.div`
    position: relative;
`;

const Overlayer = styled.div`
    position: absolute;
    top: 0;
    z-index: 10;
    width: 100%;
    height: 100%;
    background-color: rgb(255 255 255 / 37%);
`;

const StepOverview = () => {
    const { notify } = useSnackbar();
    const [additions] = useCalculatorStore((state) => [state.additions]);
    const [setEditMode] = useStepButtonsStore((state) => [state.setEditMode]);

    const [
        productIsPremium,
        productHasChanged,
        setProductIsPremiumStorageToState,
        productErrors,
        storeProductIsPremiumToStorage,
        storeProductToServer,
        setProductErrors,
    ] = useStepProductChoiceStore((state) => [
        state.productIsPremium,
        state.productHasChanged,
        state.setProductIsPremiumStorageToState,
        state.errors,
        state.storeProductIsPremiumToStorage,
        state.storeProductToServer,
        state.setErrors,
    ]);

    const [
        amount,
        amountErrors,
        setAmountStorageToState,
        storeAmountToStorage,
        storeAmountToServer,
        setAmountErrors,
    ] = useAmountStore((state) => [
        state.amount,
        state.errors,
        state.setAmountStorageToState,
        state.storeAmountToStorage,
        state.storeAmountToServer,
        state.setErrors,
    ]);

    const [prices, checkoutData, setCheckoutData, detailsHasChanged] =
        useCheckoutStore((state) => [
            state.getPrices(),
            state.checkoutData,
            state.setCheckoutData,
            state.detailsHasChanged,
        ]);

    const [
        paymentType,
        setPaymentTypeStorageToState,
        paymentTypeHasChanged,
        storePaymentTypeToStorage,
        storePaymentTypeToServer,
    ] = useStepPaymentTypeStore((state) => [
        state.paymentType,
        state.setPaymentTypeStorageToState,
        state.paymentTypeHasChanged,
        state.storePaymentTypeToStorage,
        state.storePaymentTypeToServer,
    ]);

    const { voucherCode, voucherValue } = useVoucherStore((state) => ({
        voucherCode: state.voucher,
        voucherValue: state.value,
    }));

    const [overlayContent, setOverlayContent, checkoutIsExpired] =
        useStepOverviewStore((state) => [
            state.overlayContent,
            state.setOverlayContent,
            state.checkoutIsExpired,
        ]);

    const [
        orderComment,
        deliveryTime,
        antiFreeze,
        climateNeutral,
        deliveryTimeWishDate,
        deliveryTimeFrame,
        hose,
        setOrderCommentStorageToState,
        setPromotionCodeStorageToState,
        setDeliveryTimeStorageToState,
        setDeliveryTimeTypeStorageToState,
        setAntiFreezeStorageToState,
        setDeliveryTimeFrameToState,
        setDeliveryTimeWishDateToState,
        setClimateNeutralStorageToState,
        setHoseStorageToState,
        storeDeliveryDetailsToServer,
        storeClimateNeutralToStorage,
        storeOrderCommentToStorage,
        storePromotionCodeToStorage,
        storeHoseToStorage,
        storeDeliveryTimeToStorage,
        storeDeliveryTimeTypeToStorage,
        storeAntiFreezeToStorage,
        storeDeliveryTimeFrameToStorage,
        storeDeliveryTimeWishDateToStorage,
        deliveryDetailsErrors,
        setDeliveryDetailsErrors,
    ] = useStepDeliveryDetailsStore((state) => [
        state.orderComment,
        state.deliveryTime,
        state.antiFreeze,
        state.climateNeutral,
        state.deliveryTimeWishDate,
        state.deliveryTimeFrame,
        state.hose,
        state.setOrderCommentStorageToState,
        state.setPromotionCodeStorageToState,
        state.setDeliveryTimeStorageToState,
        state.setDeliveryTimeTypeStorageToState,
        state.setAntiFreezeStorageToState,
        state.setDeliveryTimeFrameToState,
        state.setDeliveryTimeWishDateToState,
        state.setClimateNeutralStorageToState,
        state.setHoseStorageToState,
        state.storeDeliveryDetailsToServer,
        state.storeClimateNeutralToStorage,
        state.storeOrderCommentToStorage,
        state.storePromotionCodeToStorage,
        state.storeHoseToStorage,
        state.storeDeliveryTimeToStorage,
        state.storeDeliveryTimeTypeToStorage,
        state.storeAntiFreezeToStorage,
        state.storeDeliveryTimeFrameToStorage,
        state.storeDeliveryTimeWishDateToStorage,
        state.errors,
        state.setErrors,
    ]);

    const [
        deliveryAddress,
        invoiceAddress,
        useInvoiceAddress,
        additionalAddresses,
        setDeliveryAddressStorageToState,
        setInvoiceAddressStorageToState,
        storeDeliveryAddressToStorage,
        storeDeliveryAddressToServer,
        storeInvoiceAddressToStorage,
        addressErrors,
        setAddressErrors,
    ] = useStepDeliveryAddressStore((state) => [
        state.deliveryAddress,
        state.invoiceAddress,
        state.useInvoiceAddress,
        state.additionalAddresses,
        state.setDeliveryAddressStorageToState,
        state.setInvoiceAddressStorageToState,
        state.storeDeliveryAddressToStorage,
        state.storeDeliveryAddressToServer,
        state.storeInvoiceAddressToStorage,
        state.errors,
        state.setErrors,
    ]);

    /**
     * Get the current payment type.
     */
    const getCurrentPaymentType = () => {
        const currentKey = Object.keys(additions.payment_types).find(
            (key) => additions.payment_types[key].id == paymentType
        );
        return additions.payment_types[currentKey]?.name ?? "";
    };

    /**
     * Returns the label for delivery time
     */
    const getDeliveryTimeLabel = () => {
        if (deliveryTimeWishDate !== "") {
            return `Wunschtermin: ${toReadableDate(deliveryTimeWishDate)}`;
        }
        if (deliveryTimeFrame !== "") {
            return `${additions.delivery_times[deliveryTime].name}, ${additions.time_frames[deliveryTimeFrame].name}`;
        }
        return additions.delivery_times[deliveryTime].name;
    };

    const overlayBackProduct = () => {
        setProductIsPremiumStorageToState();
        productHasChanged();
        setOverlayContent("");
        setProductErrors([]);
    };

    const overlaySaveProduct = async (onSuccess) => {
        const localOnSuccess = () => {
            storeProductIsPremiumToStorage();
            onSuccess();
        };
        const checkoutDataResponse = await storeProductToServer(
            checkoutData,
            notify,
            false,
            true,
            localOnSuccess
        );
        setCheckoutData(checkoutDataResponse, true);
    };

    const overlayBackAmount = () => {
        setAmountStorageToState();
        setOverlayContent("");
        setAmountErrors([]);
    };

    const overlaySaveAmount = async (onSuccess) => {
        const localOnSuccess = () => {
            storeAmountToStorage();

            if (onSuccess) {
                onSuccess();
            }
        };
        // Store to server
        const checkoutDataResponse = await storeAmountToServer(
            checkoutData,
            notify,
            localOnSuccess
        );
        setCheckoutData(checkoutDataResponse, true);
    };

    const overlayBackDeliveryDetails = () => {
        setOrderCommentStorageToState();
        setPromotionCodeStorageToState();
        setClimateNeutralStorageToState();
        setHoseStorageToState();
        setDeliveryTimeStorageToState();
        setDeliveryTimeTypeStorageToState();
        setAntiFreezeStorageToState();
        setDeliveryTimeFrameToState();
        setDeliveryTimeWishDateToState();
        setDeliveryDetailsErrors([]);
        detailsHasChanged();
    };

    const overlaySaveDeliveryDetails = async (onSuccess) => {
        const localOnSuccess = () => {
            storeClimateNeutralToStorage();
            storeOrderCommentToStorage();
            storePromotionCodeToStorage();
            storeHoseToStorage();
            storeDeliveryTimeToStorage();
            storeDeliveryTimeTypeToStorage();
            storeAntiFreezeToStorage();
            storeDeliveryTimeFrameToStorage();
            storeDeliveryTimeWishDateToStorage();
            if (onSuccess) {
                onSuccess();
            }
        };
        // Store to server
        const checkoutDataResponse = await storeDeliveryDetailsToServer(
            checkoutData,
            notify,
            false,
            true,
            localOnSuccess
        );
        setCheckoutData(checkoutDataResponse, true);
    };

    const overlayBackPaymentType = async () => {
        setPaymentTypeStorageToState();
        paymentTypeHasChanged();
    };

    const overlaySavePaymentType = async (onSuccess) => {
        // Store to server
        const localOnSuccess = () => {
            storePaymentTypeToStorage();
            if (onSuccess) {
                onSuccess();
            }
        };
        const checkoutDataResponse = await storePaymentTypeToServer(
            checkoutData,
            notify,
            false,
            true,
            localOnSuccess
        );
        setCheckoutData(checkoutDataResponse, true);
    };

    const overlayBackDeliveryAddress = async () => {
        setDeliveryAddressStorageToState();
        if (useInvoiceAddress) {
            setInvoiceAddressStorageToState();
        }
        setAddressErrors([]);
    };

    const overlaySaveDeliveryAddress = async (onSuccess) => {
        const localOnSuccess = () => {
            storeDeliveryAddressToStorage();
            if (useInvoiceAddress) {
                storeInvoiceAddressToStorage();
            }
            if (onSuccess) {
                onSuccess();
            }
        };
        // Store to server
        const checkoutDataResponse = await storeDeliveryAddressToServer(
            checkoutData,
            notify,
            true,
            localOnSuccess
        );
        await setCheckoutData(checkoutDataResponse, true);
    };

    return (
        <Margin Component="section" top="10">
            <Wrapper>
                {checkoutIsExpired && <Overlayer />}
                <NewHeadline>Bestelldetails</NewHeadline>
                <OrderChangableDetail
                    label={
                        productIsPremium
                            ? "Eco-Super-Heizöl"
                            : "Heizöl Standard"
                    }
                    changeContent={<StepProductChoice />}
                    onOverlayInit={() => {
                        setOverlayContent("produkt");
                    }}
                    gtag="hel:checkout:overview:product"
                    overlayContent={overlayContent}
                    overlayBack={overlayBackProduct}
                    overlaySave={overlaySaveProduct}
                    formErrors={productErrors}
                    readOnly={checkoutIsExpired}
                />
                <OrderChangableDetail
                    label={`Menge ${amount}l`}
                    changeContent={<Amount />}
                    onOverlayInit={() => {
                        setOverlayContent("menge");
                    }}
                    gtag="hel:checkout:overview:amount"
                    overlayContent={overlayContent}
                    overlayBack={overlayBackAmount}
                    overlaySave={overlaySaveAmount}
                    formErrors={amountErrors}
                    readOnly={checkoutIsExpired}
                />
                {additions && (
                    <OrderChangableDetail
                        label={getDeliveryTimeLabel()}
                        changeContent={<DeliveryTime />}
                        onOverlayInit={() => {
                            setOverlayContent("lieferdetails:lieferzeit");
                        }}
                        gtag="hel:checkout:overview:delivery-time"
                        overlayContent={overlayContent}
                        overlayBack={overlayBackDeliveryDetails}
                        overlaySave={overlaySaveDeliveryDetails}
                        formErrors={deliveryDetailsErrors}
                        readOnly={checkoutIsExpired}
                    />
                )}
                <OrderChangableDetail
                    label={`Klimaneutral: ${climateNeutral ? "Ja" : "Nein"}`}
                    changeContent={<ClimateNeutral />}
                    onOverlayInit={() => {
                        setOverlayContent("lieferdetails:klimaneutral");
                    }}
                    gtag="hel:checkout:overview:climate-neutral"
                    overlayContent={overlayContent}
                    overlayBack={overlayBackDeliveryDetails}
                    overlaySave={overlaySaveDeliveryDetails}
                    formErrors={deliveryDetailsErrors}
                    readOnly={checkoutIsExpired}
                />
                <OrderChangableDetail
                    label={`Kälteschutz: ${
                        antiFreeze > 0
                            ? String(antiFreeze) +
                              ` Flasche${antiFreeze > 1 ? "n" : ""}`
                            : "Nein"
                    }`}
                    changeContent={<AntiFreeze />}
                    onOverlayInit={() => {
                        setOverlayContent("lieferdetails:frostschutz");
                    }}
                    gtag="hel:checkout:overview:anti-freeze"
                    overlayContent={overlayContent}
                    overlayBack={overlayBackDeliveryDetails}
                    overlaySave={overlaySaveDeliveryDetails}
                    formErrors={deliveryDetailsErrors}
                    readOnly={checkoutIsExpired}
                />
                {additions && (
                    <OrderChangableDetail
                        label={`Schlauchlänge ${additions.hoses[hose].name}`}
                        changeContent={<Hose />}
                        onOverlayInit={() => {
                            setOverlayContent("lieferdetails:schlauchlänge");
                        }}
                        gtag="hel:checkout:overview:hose"
                        overlayContent={overlayContent}
                        overlayBack={overlayBackDeliveryDetails}
                        overlaySave={overlaySaveDeliveryDetails}
                        formErrors={deliveryDetailsErrors}
                        readOnly={checkoutIsExpired}
                    />
                )}
                {additions && (
                    <OrderChangableDetail
                        label={getCurrentPaymentType()}
                        changeContent={<StepPaymentType />}
                        onOverlayInit={() => {
                            setOverlayContent("zahlart");
                        }}
                        gtag="hel:checkout:overview:payment-type"
                        overlayContent={overlayContent}
                        overlayBack={overlayBackPaymentType}
                        overlaySave={overlaySavePaymentType}
                        formErrors={deliveryDetailsErrors}
                        readOnly={checkoutIsExpired}
                    />
                )}
                <OrderChangableDetail
                    label={orderComment ? orderComment : "Keine Bemerkung"}
                    changeContent={<Comment />}
                    onOverlayInit={() => {
                        setOverlayContent("lieferdetails:kommentar");
                    }}
                    gtag="hel:checkout:overview:comment"
                    overlayContent={overlayContent}
                    overlayBack={overlayBackDeliveryDetails}
                    overlaySave={overlaySaveDeliveryDetails}
                    formErrors={deliveryDetailsErrors}
                    readOnly={checkoutIsExpired}
                />

                <OrderChangableDetail
                    label="Kontaktdaten"
                    value={
                        <div>
                            {deliveryAddress.salutation}{" "}
                            {deliveryAddress.firstname}{" "}
                            {deliveryAddress.lastname}
                            {deliveryAddress.email && (
                                <>
                                    <br /> {deliveryAddress.email}
                                </>
                            )}
                            {deliveryAddress.phone && (
                                <>
                                    <br /> {deliveryAddress.phone}
                                </>
                            )}
                        </div>
                    }
                    changeContent={<ContactData />}
                    onOverlayInit={() => {
                        setOverlayContent("lieferadresse:kontakt");
                    }}
                    gtag="hel:checkout:overview:contact"
                    overlayContent={overlayContent}
                    overlayBack={overlayBackDeliveryAddress}
                    overlaySave={overlaySaveDeliveryAddress}
                    formErrors={addressErrors}
                    readOnly={checkoutIsExpired}
                />

                <OrderChangableDetail
                    label="Lieferadresse"
                    value={
                        <div>
                            {deliveryAddress.street}{" "}
                            {deliveryAddress.house_number}
                            <br />
                            {deliveryAddress.zipcode} {deliveryAddress.city}
                        </div>
                    }
                    onClick={() => {
                        setEditMode(true);
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        void navigate(
                            resolveWebView("/heizoel/bestellung/lieferadresse")
                        );
                    }}
                    changeContent={<DeliveryAddress />}
                    onOverlayInit={() => {
                        setOverlayContent("lieferadresse:adresse");
                    }}
                    gtag="hel:checkout:overview:delivery-address"
                    overlayContent={overlayContent}
                    overlayBack={overlayBackDeliveryAddress}
                    overlaySave={overlaySaveDeliveryAddress}
                    formErrors={addressErrors}
                    readOnly={checkoutIsExpired}
                />

                {(additionalAddresses.length > 0 ||
                    overlayContent ===
                        "lieferadresse:weitere-entladestellen") && (
                    <OrderChangableDetail
                        label="Weitere Haushalte"
                        value={
                            <div>
                                {additionalAddresses.map(
                                    (additionalAddress, index) => {
                                        return (
                                            <div key={`aa${index}`}>
                                                <strong>
                                                    Haushalt {Number(index) + 2}
                                                </strong>
                                                <br />
                                                {
                                                    additionalAddress.firstname
                                                }{" "}
                                                {additionalAddress.lastname}
                                                <br />
                                                {additionalAddress.street}{" "}
                                                {additionalAddress.house_number}
                                                <br />
                                                {additionalAddress.city}
                                                <br />
                                                {additionalAddress.phone}
                                                <br />
                                                {additionalAddress.amount} Liter
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        }
                        changeContent={<MultipleUnloadingPoints />}
                        onOverlayInit={() => {
                            setOverlayContent(
                                "lieferadresse:weitere-entladestellen"
                            );
                        }}
                        gtag="hel:checkout:overview:delivery-address"
                        overlayContent={overlayContent}
                        overlayBack={overlayBackDeliveryAddress}
                        overlaySave={overlaySaveDeliveryAddress}
                        formErrors={addressErrors}
                        readOnly={checkoutIsExpired}
                    />
                )}

                {useInvoiceAddress && (
                    <OrderChangableDetail
                        label="Rechnungsadresse"
                        value={
                            <div>
                                {invoiceAddress.salutation}{" "}
                                {invoiceAddress.firstname}{" "}
                                {invoiceAddress.lastname}
                                <br />
                                {invoiceAddress.street}{" "}
                                {invoiceAddress.house_number}
                                <br />
                                {invoiceAddress.zipcode} {invoiceAddress.city}
                            </div>
                        }
                        changeContent={<InvoiceAddress />}
                        onOverlayInit={() => {
                            setOverlayContent("lieferadresse");
                        }}
                        gtag="hel:checkout:overview:invoice-address"
                        overlayContent={overlayContent}
                        overlayBack={overlayBackDeliveryAddress}
                        overlaySave={overlaySaveDeliveryAddress}
                        formErrors={addressErrors}
                        readOnly={checkoutIsExpired}
                    />
                )}
                {!checkoutIsExpired && (
                    <>
                        <Section>
                            <Voucher />
                        </Section>

                        <Agb />
                        <Newsletter />

                        {prices.priceTotalWithAction ? (
                            <>
                                <div className={styles.totalPrice}>
                                    Gesamtpreis:{" "}
                                    <span className={styles.oldPrice}>
                                        {prices.priceTotalGross}&nbsp;€
                                    </span>
                                </div>
                                <div className={styles.reduction}>
                                    Abzüglich:{" "}
                                    <span className={styles.reductionValue}>
                                        -{prices.priceActionSave}&nbsp;€
                                    </span>
                                </div>
                                <div
                                    className={cls(
                                        styles.totalPrice,
                                        styles.finalPrice
                                    )}
                                >
                                    Gesamtpreis: {prices.priceTotalWithAction}
                                    &nbsp;€*
                                </div>
                            </>
                        ) : voucherCode ? (
                            <>
                                <div className={styles.totalPrice}>
                                    Gesamtpreis:{" "}
                                    <span className={styles.oldPrice}>
                                        {prices.priceTotalGross}&nbsp;€
                                    </span>
                                </div>
                                <div className={styles.reduction}>
                                    Abzüglich Gutschein:{" "}
                                    <span className={styles.reductionValue}>
                                        -{roundNumber(voucherValue)}&nbsp;€
                                    </span>
                                </div>
                                <div
                                    className={cls(
                                        styles.totalPrice,
                                        styles.finalPrice
                                    )}
                                >
                                    Gesamtpreis:{" "}
                                    {roundNumber(
                                        formattedToNumber(
                                            prices.priceTotalGross
                                        ) - voucherValue
                                    )}
                                    &nbsp;€*
                                </div>
                            </>
                        ) : (
                            <div className={styles.totalPrice}>
                                Gesamtpreis: {prices.priceTotalGross}&nbsp;€
                            </div>
                        )}
                    </>
                )}
            </Wrapper>
            <CheckoutIsExpired />
        </Margin>
    );
};

export default StepOverview;
